import {useDispatch, useSelector} from "react-redux";
import {useEffect, useLayoutEffect, useMemo, useRef, VideoHTMLAttributes} from "react";
import {StatusAction} from "../state/status";
import {StreamError} from "../state/types";
import {PlayerElement, PlayerElementHandle} from "./PlayerElement";
import {AppState} from "../state";
import equals from "fast-deep-equal";
import Style from "./style.module.scss";
import {useProgress} from "../progress";

let progressCalled = false;
let waitingCalled = false;

export const useMedia = () => {

    const vPlayer = useRef<PlayerElementHandle>(null);
    const vProgress = useRef<any>(0);

    const dispatch = useDispatch();
    const {source, status, position, speed} = useSelector((state: AppState) => ({source: state.source, status: state.status, position: state.position, speed: state.speed}), equals);
    const progress = useProgress();
    const vWait = useRef<number[]>([]);
    const playerId = useSelector((state: AppState)=> state.id);
    const id = useRef(playerId);
    id.current = playerId;

    const search = (e: React.SyntheticEvent<HTMLVideoElement>|undefined = undefined) => vPlayer.current ||  e?.currentTarget ||  (document.getElementById(`videoplayerradiacz-${id.current}`) as HTMLVideoElement|null) || undefined;


    const props = useMemo(() => {
        const props = {
           onWaiting: (e) => {
                waitingCalled = true;

                vWait.current = [];
                const player = search(e);

                if ((player?.readyState || 0) <= 2 && status !== "LOADING") {
                    dispatch<StatusAction>({
                        type: "Status",
                        payload: "LOADING"
                    })
                }
            },
            onEnded: (e) => {
               dispatch<StatusAction>({
                   type: "Status",
                   payload: "STOPPED",
               })
            },
            onTimeUpdate: (e)=> {
                const player = search(e);
                if (player)
                    progress.trigger(player.currentTime, player.duration);
                if (status !== "LOADING") {
                    vWait.current = [];
                } else if (vWait.current.length > 5) {

                    vWait.current.shift();
                    if (player)
                        vWait.current.push(player.currentTime);
                    const sum = vWait.current.map((x,k)=>{
                        if (k === 0){
                            return 0;
                        }
                        return x - vWait.current[k-1];
                    }).reduce((x, acc) => acc + x, 0);

                    if (sum / (vWait.current.length -1) < 1 && sum > .1) {
                        dispatch<StatusAction>({
                            payload: "PLAYING",
                            type: "Status"
                        })
                    }

                } else {
                    if (player)
                        vWait.current.push(player.currentTime);
                }
            },
            onPause: () => {
                dispatch<StatusAction>({
                    type: "Status",
                    payload: "STOPPED"
                });
            },
            onProgress: (e) => {
                progressCalled = true;
                const player = search(e);
                if ((player?.readyState || 0) > 2 && status !== "PLAYING") {
                    dispatch<StatusAction>({
                        type: "Status",
                        payload: "PLAYING"
                    });
                }
            },
            onError: (e) => {
                dispatch<StreamError>({
                    type: "StreamError"
                })
            }
        } as VideoHTMLAttributes<HTMLVideoElement>;
        return props;
        // eslint-disable-next-line
    }, [dispatch, status])

    const vTimes = useRef<number[]>([]);

    useLayoutEffect(() => {
        if (vProgress.current) {
            clearInterval(vProgress.current);
            vProgress.current = 0;
        }

        if (source.type === 'Audio' && (!progressCalled && !waitingCalled)) {
            const player = search();
            let current = player?.currentTime || vTimes.current[vTimes.current.length - 1] || 0;
            vProgress.current = setInterval(() => {
                if (progressCalled && waitingCalled) {
                    clearInterval(vProgress.current);
                    vProgress.current = 0;
                    return;
                }

                if (player)
                    progress.trigger(player.currentTime, player.duration);

                const nextTime = player?.currentTime || vTimes.current[vTimes.current.length - 1] || 0;
                if (nextTime < current) {
                    vTimes.current = [];
                } else {
                    vTimes.current.push(nextTime - current);
                }
                current = nextTime;

                if (vTimes.current.length < 11) {
                    if (status !== "LOADING")
                    {
                        dispatch<StatusAction>({
                            type: "Status",
                            payload: "LOADING"
                        });
                    }
                    return;
                }
                vTimes.current.shift();
                const sum = vTimes.current.reduce((acc, i)=> acc+i, 0) / 10;

                if (sum > 0.250 || (player?.readyState || 0) > 2) {
                    if (status === "LOADING") {
                        dispatch<StatusAction>({
                            type: "Status",
                            payload: "PLAYING"
                        });
                    }
                } else if (status === "PLAYING") {
                    dispatch<StatusAction>({
                        type: "Status",
                        payload: "LOADING"
                    });
                }
            }, 300);
        }
        // eslint-disable-next-line
    }, [source.type, status, dispatch]);

    useEffect(() => {
        return () => {
            if (vProgress.current)
                clearInterval(vProgress.current);
        }
    }, []);


    if (source.type === "Audio") {
        const useMediaInner = () => (<div>
            <div key={'container'} className={Style.myPlayer}>{[
                <PlayerElement key={'video'}
                               sources={[{src: source.content.src, type: source.content.type}]}
                               position={position}
                               speed={speed}
                               ref={vPlayer} {...props}/>
            ]}</div>
        </div>)

        return useMediaInner ;
    }

    return false;
}